/**
 * Patients API Client
 * ====================
 * API functions for patient operations.
 */

import { apiClient } from './client';
import type { Patient, PatientBrief, PaginatedResponse, ChartBrief } from '@/types';

// =============================================================================
// TYPES
// =============================================================================

export interface PatientCreateRequest {
  first_name: string;
  middle_name?: string;
  last_name: string;
  preferred_name?: string;
  date_of_birth?: string;
  gender?: string;
  email?: string;
  phone_primary?: string;
  phone_secondary?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  chart_number?: string;
  legacy_patient_id?: string;
}

export interface PatientUpdateRequest {
  first_name?: string;
  middle_name?: string;
  last_name?: string;
  preferred_name?: string;
  date_of_birth?: string;
  gender?: string;
  email?: string;
  phone_primary?: string;
  phone_secondary?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  chart_number?: string;
  is_active?: boolean;
}

export interface PatientSearchParams {
  query?: string;
  is_active?: boolean;
  risk_level?: string;
  referred?: boolean;
  page?: number;
  page_size?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

export interface PatientRiskUpdate {
  risk_level: 'low' | 'moderate' | 'high';
  risk_notes?: string;
}

export interface PatientReferralUpdate {
  referred_to_periodontist: boolean;
  referral_date?: string;
  referral_notes?: string;
}

export interface PatientLookupResponse {
  found: boolean;
  patient?: Patient;
  created?: boolean;
}

// =============================================================================
// API FUNCTIONS
// =============================================================================

/**
 * List patients with pagination and filters.
 */
export async function listPatients(
  params: PatientSearchParams = {}
): Promise<PaginatedResponse<PatientBrief>> {
  const response = await apiClient.get<PaginatedResponse<PatientBrief>>('/patients', {
    params,
  });
  return response.data;
}

/**
 * Get patient by ID.
 */
export async function getPatient(
  patientId: string,
  includeCharts = true
): Promise<Patient> {
  const response = await apiClient.get<Patient>(`/patients/${patientId}`, {
    params: { include_charts: includeCharts },
  });
  return response.data;
}

/**
 * Get patient by legacy ID.
 */
export async function getPatientByLegacyId(
  legacyId: string
): Promise<PatientLookupResponse> {
  const response = await apiClient.get<PatientLookupResponse>(
    `/patients/by-legacy-id/${legacyId}`
  );
  return response.data;
}

/**
 * Lookup or create patient by legacy ID.
 */
export async function lookupOrCreatePatient(
  legacyPatientId: string,
  createIfNotFound: boolean,
  patientData?: PatientCreateRequest
): Promise<PatientLookupResponse> {
  const response = await apiClient.post<PatientLookupResponse>('/patients/lookup', {
    legacy_patient_id: legacyPatientId,
    create_if_not_found: createIfNotFound,
    ...patientData,
  });
  return response.data;
}

/**
 * Create a new patient.
 */
export async function createPatient(data: PatientCreateRequest): Promise<Patient> {
  const response = await apiClient.post<Patient>('/patients', data);
  return response.data;
}

/**
 * Update patient.
 */
export async function updatePatient(
  patientId: string,
  data: PatientUpdateRequest
): Promise<Patient> {
  const response = await apiClient.patch<Patient>(`/patients/${patientId}`, data);
  return response.data;
}

/**
 * Delete patient (soft delete).
 */
export async function deletePatient(patientId: string): Promise<void> {
  await apiClient.delete(`/patients/${patientId}`);
}

/**
 * Update patient risk assessment.
 */
export async function updatePatientRisk(
  patientId: string,
  data: PatientRiskUpdate
): Promise<Patient> {
  const response = await apiClient.patch<Patient>(
    `/patients/${patientId}/risk`,
    data
  );
  return response.data;
}

/**
 * Update patient referral status.
 */
export async function updatePatientReferral(
  patientId: string,
  data: PatientReferralUpdate
): Promise<Patient> {
  const response = await apiClient.patch<Patient>(
    `/patients/${patientId}/referral`,
    data
  );
  return response.data;
}

/**
 * Search patients (quick search).
 */
export async function searchPatients(
  query: string,
  limit = 10
): Promise<PatientBrief[]> {
  const response = await apiClient.get<PaginatedResponse<PatientBrief>>('/patients', {
    params: {
      query,
      page_size: limit,
    },
  });
  return response.data.items;
}

/**
 * Get patient's chart history.
 */
export async function getPatientCharts(
  patientId: string,
  includeDrafts = true
): Promise<ChartBrief[]> {
  const response = await apiClient.get<ChartBrief[]>(
    `/charts/patient/${patientId}`,
    {
      params: { include_drafts: includeDrafts },
    }
  );
  return response.data;
}
